import React, { useMemo, useState } from "react";
import "./CreateByeModal.css";

function CreateByeModal({
  leagueType,
  teams,
  players,
  handleCreateBye,
  setIsByeModalOpen,
}) {
  const isIndividual = leagueType === "individual";

  const participantLabel = isIndividual ? "Player" : "Team";
  const optionLabel = isIndividual ? "Select player" : "Select team";
  const participants = isIndividual ? players : teams;

  const createRow = () => ({
    rowKey: `${Date.now()}-${Math.random()}`,
    participantId: "",
  });

  const createInitialRows = () => {
    return Array.from({ length: 5 }, () => createRow());
  };

  const [byeDate, setByeDate] = useState("");
  const [byeRows, setByeRows] = useState(createInitialRows());
  const [isSaving, setIsSaving] = useState(false);

  const getParticipantId = (participant) => {
    return isIndividual
      ? participant.id
      : participant.seasons_leagues_teams_id;
  };

  const getParticipantName = (participant) => {
    if (isIndividual) {
      const player = participant.player || participant;

      return (
        player?.name ||
        player?.full_name ||
        `${player?.firstName || ""} ${player?.lastName || ""}`.trim() ||
        `${player?.first_name || ""} ${player?.last_name || ""}`.trim() ||
        player?.username ||
        "Unnamed Player"
      );
    }

    return participant.name || participant.team?.name || "Unnamed Team";
  };

  const participantOptions = useMemo(() => {
    return participants.map((participant) => ({
      id: getParticipantId(participant),
      name: getParticipantName(participant),
    }));
  }, [participants, isIndividual]);

  const updateRow = (rowKey, value) => {
    setByeRows((prev) =>
      prev.map((row) =>
        row.rowKey === rowKey
          ? { ...row, participantId: value }
          : row
      )
    );
  };

  const addAnotherBye = () => {
    setByeRows((prev) => [...prev, createRow()]);
  };

  const removeByeRow = (rowKey) => {
    setByeRows((prev) => {
      if (prev.length === 1) return prev;
      return prev.filter((row) => row.rowKey !== rowKey);
    });
  };

  const handleSaveByes = async () => {
    if (!byeDate) {
      return alert("Date is required.");
    }

    const filledRows = byeRows.filter((row) => row.participantId);

    if (!filledRows.length) {
      return alert(`Select at least one ${participantLabel.toLowerCase()}.`);
    }

    const participantIds = filledRows.map((row) => Number(row.participantId));
    const uniqueParticipantIds = [...new Set(participantIds)];

    if (uniqueParticipantIds.length !== participantIds.length) {
      return alert(`Remove duplicate ${participantLabel.toLowerCase()} selections.`);
    }

    setIsSaving(true);

    await handleCreateBye({
      date: byeDate,
      participantIds: uniqueParticipantIds,
    });

    setIsSaving(false);
  };

  return (
    <div className="createByeModalPage-overlay">
      <div className="createByeModalPage-modal">
        <div className="createByeModalPage-header">
          <div>
            <p className="createByeModalPage-eyebrow">Schedule Bye</p>
            <h3 className="createByeModalPage-title">Add Byes</h3>
            <p className="createByeModalPage-subtitle">
              Choose a bye date, then add one or multiple {isIndividual ? "players" : "teams"}.
            </p>
          </div>

          <button
            type="button"
            className="createByeModalPage-closeBtn"
            onClick={() => setIsByeModalOpen(false)}
          >
            ×
          </button>
        </div>

        <div className="createByeModalPage-dateCard">
          <div className="createByeModalPage-sectionHeader">
            <h4>Bye Date</h4>
            <p>All {isIndividual ? "players" : "teams"} below will use this date.</p>
          </div>

          <label className="createByeModalPage-field">
            <span>Bye Date</span>
            <input
              type="date"
              value={byeDate}
              onChange={(e) => setByeDate(e.target.value)}
            />
          </label>
        </div>

        <div className="createByeModalPage-listCard">
          <div className="createByeModalPage-sectionHeader">
            <h4>{isIndividual ? "Players" : "Teams"}</h4>
            <p>Leave unused rows blank. Only selected rows will be saved.</p>
          </div>

          <div className="createByeModalPage-rowsHeader">
            <span>{participantLabel}</span>
            <span></span>
          </div>

          <div className="createByeModalPage-rowList">
            {byeRows.map((row, index) => (
              <div key={row.rowKey} className="createByeModalPage-row">
                <div className="createByeModalPage-mobileRowNumber">
                  Bye {index + 1}
                </div>

                <label className="createByeModalPage-rowField">
                  <span>{participantLabel}</span>
                  <select
                    value={row.participantId}
                    onChange={(e) => updateRow(row.rowKey, e.target.value)}
                  >
                    <option value="">{optionLabel}</option>

                    {participantOptions.map((participant) => (
                      <option key={participant.id} value={participant.id}>
                        {participant.name}
                      </option>
                    ))}
                  </select>
                </label>

                <button
                  type="button"
                  className="createByeModalPage-removeBtn"
                  onClick={() => removeByeRow(row.rowKey)}
                  disabled={byeRows.length === 1}
                  title="Remove bye row"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="createByeModalPage-footer">
          <button
            type="button"
            className="createByeModalPage-secondaryBtn"
            onClick={addAnotherBye}
          >
            + Add More Byes
          </button>

          <div className="createByeModalPage-footerRight">
            <button
              type="button"
              className="createByeModalPage-cancelBtn"
              onClick={() => setIsByeModalOpen(false)}
            >
              Cancel
            </button>

            <button
              type="button"
              className="createByeModalPage-primaryBtn"
              onClick={handleSaveByes}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save Byes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreateByeModal;